import React, { useState, useEffect } from "react";
import Translator, { __ } from "services/Translator";
import { Dropdown } from "react-bootstrap";
import api from "services/api";
import { Helmet } from "react-helmet";
import config from "configs/config.js";

const chevronSvg = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M4.293 8.293a1 1 0 0 1 1.414 0L12 14.586l6.293-6.293a1 1 0 1 1 1.414 1.414l-7 7a1 1 0 0 1-1.414 0l-7-7a1 1 0 0 1 0-1.414"
      fill="currentColor"
    />
  </svg>
);

function languageShortLabel(value) {
  const c = value.countries != null ? String(value.countries).trim() : "";
  if (c.length >= 2) {
    return c.toUpperCase().slice(0, 2);
  }
  const code = value.code != null ? String(value.code).trim() : "";
  return code.toUpperCase().slice(0, 2) || "—";
}

function resolveFlagPath(flagPath) {
  const raw = flagPath != null ? String(flagPath).trim() : "";
  if (!raw) return "";
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/")) {
    if (config.publicDir) {
      return `${config.publicDir}${raw}`;
    }
    return raw;
  }
  if (config.publicDir) {
    return `${config.publicDir}/${raw}`;
  }
  return raw;
}

export default function LanguageSwitcherDropdown(props) {
  const [languages, setLanguages] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const selectedLanguage = Translator.getSelectedLanguage();
  const [appUrl, setAppUrl] = useState(config.appUrl);
  const inSidebar = props.sidebarVariant === true;
  const sidebarFoldedIn = inSidebar && props.isSidebarCollapsed === true;
  const useMobileInlinePanel =
    inSidebar && props.isMobileDevice === true && !sidebarFoldedIn;
  const dropDirection = inSidebar
    ? sidebarFoldedIn
      ? "end"
      : "up"
    : undefined;

  const getLanguages = async () => {
    var response = await api.get("/languages");
    if (response.status === 200) {
      setLanguages(response.data);
    } else {
      props.showAlert(null, response.data.message);
    }
  };

  // Persist the pick on the account as well, so the CRM (Customer.io/Smartico)
  // and transactional email stop using the language captured at sign-up. Only
  // for a logged-in player; a failure must never block the switch itself.
  const persistLanguage = async (lang) => {
    if (!props.isAuthenticated || !lang || !lang.code) return;
    try {
      await api.post("/player/language", { language: lang.code });
    } catch (_) {
      // ignore — the local switch still applies
    }
  };

  const switchLanguage = async (lang) => {
    Translator.setSelectedLanguage(lang);
    await persistLanguage(lang);
    window.location.reload();
  };

  useEffect(() => {
    getLanguages();
  }, []);

  useEffect(() => {
    if (sidebarFoldedIn && menuOpen) {
      setMenuOpen(false);
    }
  }, [sidebarFoldedIn, menuOpen]);

  const defaultFlagSrc = resolveFlagPath(
    config.defaultLanguage?.flag_path || "/images/flags/svg/gb.svg"
  );
  const flagSrc = resolveFlagPath(selectedLanguage?.flag_path) || defaultFlagSrc;

  const rootClass = [
    "languageSwitcherDropdown",
    inSidebar ? "languageSwitcherDropdown--sidebar" : "",
    inSidebar && props.isSidebarCollapsed ? "languageSwitcherDropdown--collapsed" : "",
    inSidebar && !props.isSidebarCollapsed ? "languageSwitcherDropdown--expanded" : "",
    useMobileInlinePanel ? "languageSwitcherDropdown--mobileInline" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const menuClassName = inSidebar
    ? "languageSwitcherDropdown__menu languageSwitcherDropdown__menu--play365"
    : "";

  const closeMenu = () => setMenuOpen(false);

  const handleLanguagePick = (value) => {
    if (inSidebar) {
      closeMenu();
    }
    switchLanguage(value);
  };

  const renderSidebarToggleLabel = () => (
    <>
      <div className="menu-item-area">
        <div className="menu-icon">
          <img
            src={flagSrc}
            alt=""
            className="flag-icon"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = defaultFlagSrc;
            }}
          />
        </div>
        <div className="menu-text language-menu-fullname">{selectedLanguage.language}</div>
        {!sidebarFoldedIn ? (
          <div className="menu-text language-menu-short">{languageShortLabel(selectedLanguage)}</div>
        ) : null}
      </div>
      {!sidebarFoldedIn ? <div className="accordion-chevron">{chevronSvg}</div> : null}
    </>
  );

  const renderMenuHeader = () => (
    <div className="languageSwitcherDropdown__menuHeader">
      <button
        type="button"
        className="languageSwitcherDropdown__menuClose"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          closeMenu();
        }}
        aria-label={__("Close")}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );

  const renderSidebarLanguageRow = (value, index, asButton) => {
    const rowClass = [
      selectedLanguage.code === value.code ? "selected active" : null,
      inSidebar ? "languageSwitcherDropdown__menuRow" : null,
    ]
      .filter(Boolean)
      .join(" ");

    const rowBody = (
      <div className="language-item-content language-item-content--play365">
        <span className="language-flag-circle">
          {value.flag_path ? (
            <img
              src={resolveFlagPath(value.flag_path)}
              alt=""
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultFlagSrc;
              }}
            />
          ) : null}
        </span>
        <span className="language-name-full">{value.language}</span>
        <span className="language-code">{languageShortLabel(value)}</span>
      </div>
    );

    if (asButton) {
      return (
        <button
          key={index}
          type="button"
          className={rowClass}
          onClick={() => handleLanguagePick(value)}
        >
          {rowBody}
        </button>
      );
    }

    return (
      <Dropdown.Item key={index} onClick={() => handleLanguagePick(value)} className={rowClass}>
        {rowBody}
      </Dropdown.Item>
    );
  };

  const renderHeaderLanguageRow = (value, index) => (
    <Dropdown.Item
      key={index}
      onClick={() => handleLanguagePick(value)}
      className={selectedLanguage.code === value.code ? "selected active" : null}
    >
      <div className="language-item-content">
        {value.flag_path && (
          <img
            src={resolveFlagPath(value.flag_path)}
            alt=""
            className="flag-icon"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = defaultFlagSrc;
            }}
          />
        )}
        <label htmlFor={value.code} className="text fullName">
          {value.language}
        </label>
      </div>
      <input
        type="radio"
        id={value.code}
        checked={selectedLanguage.code === value.code ? true : false}
        name="lang"
        readOnly
      />
    </Dropdown.Item>
  );

  const renderLanguageList = (sidebarRowsAsButtons) => {
    if (languages.length === 0) {
      return null;
    }
    return languages.map((value, index) =>
      inSidebar
        ? renderSidebarLanguageRow(value, index, sidebarRowsAsButtons)
        : renderHeaderLanguageRow(value, index)
    );
  };

  const renderSidebarMenuBody = (sidebarRowsAsButtons) => (
    <>
      {inSidebar && renderMenuHeader()}
      {renderLanguageList(sidebarRowsAsButtons)}
    </>
  );

  return (
    <>
      <Helmet>
        {languages.length > 0 &&
          languages.map((value, index) => (
            <link
              key={index}
              rel="alternate"
              hreflang={value.code + "-" + value.countries}
              href={appUrl + "/" + value.code}
            />
          ))}
      </Helmet>
      <div className={rootClass}>
        {useMobileInlinePanel ? (
          <>
            <button
              type="button"
              className={`sidebar-accordion-button languageSwitcherDropdown__toggle${menuOpen ? " show" : ""}`}
              id="dropdown-language"
              aria-label={__("Language")}
              aria-expanded={menuOpen}
              aria-controls="language-switcher-inline-panel"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {renderSidebarToggleLabel()}
            </button>
            {menuOpen ? (
              <div
                id="language-switcher-inline-panel"
                className={`${menuClassName} languageSwitcherDropdown__inlinePanel`}
                role="listbox"
                aria-label={__("Language")}
              >
                {renderSidebarMenuBody(true)}
              </div>
            ) : null}
          </>
        ) : (
          <Dropdown show={menuOpen} onToggle={setMenuOpen} drop={dropDirection}>
            {inSidebar ? (
              <Dropdown.Toggle
                variant="link"
                className="sidebar-accordion-button languageSwitcherDropdown__toggle"
                id="dropdown-language"
                aria-label={__("Language")}
              >
                {renderSidebarToggleLabel()}
              </Dropdown.Toggle>
            ) : (
              <Dropdown.Toggle
                variant="language"
                id="dropdown-language"
                aria-label={__("Language")}
              >
                {/* The flag is decoration, never the only thing on the toggle:
                    the default flag_path is built from config.publicDir, which
                    is not always a reachable host, and a lone broken image
                    makes the whole control invisible. Hide a failed image and
                    keep the language name as the label. */}
                <img
                  src={flagSrc}
                  alt=""
                  className="flag-icon"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = "none";
                  }}
                />
                <span className="language-toggle-label">
                  {selectedLanguage.language || languageShortLabel(selectedLanguage)}
                </span>
              </Dropdown.Toggle>
            )}

            <Dropdown.Menu align="start" className={menuClassName || undefined}>
              {inSidebar ? renderSidebarMenuBody(false) : renderLanguageList(false)}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    </>
  );
}
